<template>
    <div class="account-page">
        <div class="main-wrapper">
            <div class="account-content">
                <div class="login-wrapper">
                    <div class="login-content">
                        <div v-if="isForcedChange||isAuthenticated" class="login-userset">                            
                            <div class="login-logo logo-normal">
                                    <img src="../../../assets/img/logo.png" alt="Logo" />
                                </div>
                                <div class="login-userheading">
                                    <h3>{{ isForcedChange ? 'Mandatory Password Update' : 'Change Password' }}</h3>
                                    <h4>
                                        {{ isForcedChange ? 'You must set a new password to continue.' : 'Update your existing credentials.' }}
                                    </h4>
                                </div>

                                <form class="login" @submit.prevent="handleChangePassword">
                                    
                                    <div class="form-login">
                                        <label>Email Address</label>
                                        <div class="form-addons">
                                            <input type="email" v-model="email" disabled required class="form-control" />
                                            <img src="../../../assets/img/icons/mail.svg" alt="img" />
                                        </div>
                                    </div>

                                    <div v-if="!isForcedChange" class="form-login">
                                        <label>Current Password</label>
                                        <div class="pass-group">
                                            <input
                                                :type="currentPasswordType"
                                                v-model="currentPassword"
                                                placeholder="Enter Current Password"
                                                autocomplete="current-password"
                                                required
                                                class="form-control pass-input"
                                            />
                                            <span @click="toggleCurrentPassword" class="fas toggle-password"
                                                :class="{ 'fa-eye': showCurrentPassword, 'fa-eye-slash': !showCurrentPassword }">
                                            </span>
                                        </div>
                                    </div>

                                    <div class="form-login">
                                        <label>New Password</label>
                                        <div class="pass-group">
                                            <input 
                                                :type="newPasswordType" 
                                                v-model="newPassword" 
                                                placeholder="Enter New Password"
                                                autocomplete="new-password"
                                                required 
                                                class="form-control pass-input"
                                            />
                                            <span @click="toggleNewPassword" class="fas toggle-password"
                                                :class="{ 'fa-eye': showNewPassword, 'fa-eye-slash': !showNewPassword }">
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div class="form-login">
                                        <label>Confirm New Password</label>
                                        <div class="pass-group">
                                            <input 
                                                type="password" 
                                                v-model="confirmNewPassword" 
                                                placeholder="Confirm New Password"
                                                autocomplete="new-password"
                                                required 
                                                class="form-control pass-input"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div v-if="statusMessage" :class="['alert', isError ? 'alert-danger' : 'alert-success', 'mt-3']">
                                        {{ statusMessage }}
                                    </div>

                                    <div class="form-login">
                                        <button class="btn btn-login" type="submit" :disabled="isLoading || !canSubmit">
                                            {{ isLoading ? 'Processing...' : 'Change Password' }}
                                        </button>
                                    </div>
                                    
                                    <div class="form-login text-center">
                                        <router-link v-if="!isForcedChange" to="/index" class="hover-a">
                                            Cancel
                                        </router-link>
                                        <router-link v-else :to="{ name: 'signin', query: { email } }" class="hover-a">
                                            Back to Sign In
                                        </router-link>
                                    </div>
                                </form>
                        </div>
                    </div>
                    <div class="login-img">
                        <img src="../../../assets/img/login.jpg" alt="img" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import axios from 'axios';
import { getUserEmail } from '@/auth/permissions';
import { clearPasswordChangeToken, getPasswordChangeToken } from '@/auth/passwordChangeToken';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 72;
const PASSWORD_LENGTH_MESSAGE = `New password must be ${PASSWORD_MIN_LENGTH}-${PASSWORD_MAX_LENGTH} characters.`;
const SESSION_EXPIRED_MESSAGE = 'Your password change session has expired. Please sign in again.';

export default {
    name: 'AuthPasswordUpdate',
    data() {
        return {
            email: this.$route.query.email || '', // Pre-fill from query (forced change) or Vuex
            passwordChangeToken: getPasswordChangeToken(),
            currentPassword: '',
            showCurrentPassword: false,
            newPassword: '',
            confirmNewPassword: '',
            showNewPassword: false,

            isLoading: false,
            statusMessage: null,
            isError: false,
        };
    },
    computed: {
        ...mapGetters(['isAuthenticated', 'getUserDetails']),
        
        isForcedChange() {
            return !!this.$route.query.force;
        },
        currentPasswordType() {
            return this.showCurrentPassword ? 'text' : 'password';
        },
        newPasswordType() {
            return this.showNewPassword ? 'text' : 'password';
        },
        canSubmit() {
            if (this.isForcedChange && !this.passwordChangeToken) {
                return false;
            }
            return this.newPassword === this.confirmNewPassword;
        }
    },
    watch: {
        isAuthenticated: {
            immediate: true,
            handler(isAuth) {
                if (!isAuth && !this.isForcedChange) {
                // Perform the redirection
                this.$router.push('/signin');
            }
                else if (isAuth && !this.isForcedChange) {
                    this.email = getUserEmail(this.getUserDetails) || '';
                }
                
            }
        },
        getUserDetails: {
        immediate: true,
        handler() {
            const userEmail = getUserEmail(this.getUserDetails);
            if (!this.$route.query.email && userEmail) {
                    this.$data.email = userEmail;
                }
            }
        },
    },
    methods: {
        ...mapMutations(['CLEAR_AUTH_DATA']),
        toggleCurrentPassword() {
            this.showCurrentPassword = !this.showCurrentPassword;
        },
        toggleNewPassword() {
            this.showNewPassword = !this.showNewPassword;
        },

        async handleChangePassword() {
            this.statusMessage = null;
            this.isError = false;
            
            if (this.newPassword !== this.confirmNewPassword) {
                this.statusMessage = 'New passwords do not match.';
                this.isError = true;
                return;
            }
            if (this.newPassword.length < PASSWORD_MIN_LENGTH || this.newPassword.length > PASSWORD_MAX_LENGTH) {
                this.statusMessage = PASSWORD_LENGTH_MESSAGE;
                this.isError = true;
                return;
            }
            if (this.isForcedChange && !this.passwordChangeToken) {
                this.statusMessage = SESSION_EXPIRED_MESSAGE;
                this.isError = true;
                return;
            }

            this.isLoading = true;

            try {
                const response = this.isForcedChange
                    ? await axios.post('/v1/auth/change-password/forced', {
                        password_change_token: this.passwordChangeToken,
                        new_password: this.newPassword,
                    })
                    : await axios.post('/v1/auth/change-password', {
                        current_password: this.currentPassword,
                        new_password: this.newPassword,
                    });

                if (response.data.status === 'SUCCESS') {
                    this.statusMessage = response.data.message || 'Password updated successfully! Please sign in with your new password.';
                    this.isError = false;

                    // The server ends the session after a password change (and the forced flow
                    // never had one), so only client auth state needs clearing.
                    clearPasswordChangeToken();
                    this.passwordChangeToken = null;
                    this.CLEAR_AUTH_DATA();

                    setTimeout(() => {
                        this.$router.push({
                        name: 'signin',
                        query: { email: this.email }
                    });
                    }, 1000);
                }

            } catch (err) {
                const responseData = err.response?.data?.detail ?? err.response?.data;
                this.isError = true;
                this.newPassword = '';
                this.confirmNewPassword = '';

                if (responseData?.status === 'PASSWORD_CHANGE_TOKEN_INVALID') {
                    clearPasswordChangeToken();
                    this.passwordChangeToken = null;
                    this.statusMessage = responseData.message || SESSION_EXPIRED_MESSAGE;
                } else if (err.response?.status === 422) {
                    this.statusMessage = PASSWORD_LENGTH_MESSAGE;
                } else {
                    this.currentPassword = '';
                    this.statusMessage = responseData?.message
                                        || (typeof responseData === 'string' ? responseData : null)
                                        || 'Invalid credentials or an unexpected error occurred.';
                }

            } finally {
                this.isLoading = false;
            }
        },
    },
    created() {
        if (!this.isAuthenticated && !this.isForcedChange) {
            this.$router.push({ name: 'signin' });
        } else if (this.isForcedChange && !this.passwordChangeToken) {
            this.statusMessage = SESSION_EXPIRED_MESSAGE;
            this.isError = true;
        }
    }
};
</script>

<style scoped>
/* Simplified CSS for messages */
.pass-group {
    position: relative;
}
.toggle-password {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    z-index: 10;
}
.alert {
    padding: 10px;
    border-radius: 4px;
    font-size: 14px;
    text-align: center;
}
.alert-danger {
    background-color: #f8d7da;
    color: #721c24;
}
.alert-success {
    background-color: #d4edda;
    color: #155724;
}
</style>
